'use client';

import React from 'react';

const WATCH_INTERVAL_MS = 300;

export interface UseWindowValueProps<T> {
  getter: (window: Window) => T;
  watch?: boolean;
  watchInterval?: number;
}

export const useWindowValue = <T,>(props: UseWindowValueProps<T>) => {
  const { getter, watch, watchInterval = WATCH_INTERVAL_MS } = props;
  const intervalRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  const [value, setValue] = React.useState<T | undefined>();

  const updateValue = React.useCallback(() => {
    const isBrowser = typeof window !== 'undefined';

    if (isBrowser) {
      const newValue = getter(window);
      setValue(() => newValue);
    }
  }, [getter]);

  React.useEffect(() => {
    updateValue();
  }, [updateValue]);

  React.useEffect(() => {
    if (watch) {
      intervalRef.current = setInterval(() => {
        updateValue();
      }, watchInterval);
    }
    return () => {
      clearInterval(intervalRef.current);
    };
  }, [watch, updateValue, watchInterval]);

  return value;
};
